import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlContainer,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { CalendarComponent } from '../date-picker-components/calendar/calendar.component';
import { fromEvent } from 'rxjs';
import { FormInputComponent } from '../../form-input.component';
import { ErrorMessageComponent } from '../../../../layout/error-message/error-message.component';
import { AppendTestIdPipe } from '../../../../../pipes/append-test-id/append-test-id.pipe';
import {
  DATE_FORMAT,
  EEntityType,
  ERelationType,
  ONE_HOUR,
  ONE_MINUTE,
  TIME_FORMAT_SHORT,
} from '@web/dtos';
import { ButtonComponent } from '../../../../feedback/button/button.component';
import { FilterTextsPipe } from '../../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../../pipes/language/language.pipe';

type JourneyDate = ERelationType.arrival | ERelationType.departure;

interface IDateForm {
  day: FormControl<string>;
  month: FormControl<string>;
  year: FormControl<string>;
  hour: FormControl<string>;
  minutes: FormControl<string>;
}

@Component({
  selector: 'aw-relation-date-input',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
    {
      provide: NgControl,
      useClass: class extends NgControl {
        control: FormControl = new FormControl();
        viewToModelUpdate() {}
      },
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    AppendTestIdPipe,
    CalendarComponent,
    ButtonComponent,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './relation-date-input.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class RelationDateInputComponent
  extends FormInputComponent
  implements OnInit, OnDestroy
{
  public readonly showTimePicker: InputSignal<boolean> = input(false);
  public readonly headline: InputSignal<string> = input.required();
  public readonly max: InputSignal<Date | undefined> = input();
  public readonly min: InputSignal<Date | undefined> = input();
  public readonly arrivalControl: InputSignal<
    FormControl<string | null> | undefined
  > = input();
  public readonly departureControl: InputSignal<
    FormControl<string | null> | undefined
  > = input();

  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  public readonly showCalendar: WritableSignal<boolean> = signal(false);
  public originalArrival: string | null = null;
  public originalDeparture: string | null = null;

  protected readonly DATE_FORMAT: string = DATE_FORMAT;
  protected readonly TIME_FORMAT_SHORT: string = TIME_FORMAT_SHORT;

  protected readonly innerControl: FormControl<string> =
    this.formBuilder.nonNullable.control<string>('');
  protected readonly departureOrArrival: FormControl<JourneyDate> =
    this.formBuilder.nonNullable.control<JourneyDate>(ERelationType.departure);

  protected readonly dateForm: FormGroup<IDateForm> = this.formBuilder.group({
    day: this.formBuilder.nonNullable.control(''),
    month: this.formBuilder.nonNullable.control(''),
    year: this.formBuilder.nonNullable.control(''),
    hour: this.formBuilder.nonNullable.control(''),
    minutes: this.formBuilder.nonNullable.control(''),
  });

  constructor(public override ngControl: NgControl) {
    super(ngControl);
    effect(() => {
      if (this.showCalendar()) {
        const arrival: FormControl<string | null> | undefined =
          this.arrivalControl();
        const departure: FormControl<string | null> | undefined =
          this.departureControl();
        if (arrival) {
          this.originalArrival = arrival.value;
        }
        if (departure) {
          this.originalDeparture = departure.value;
        }
      }
    });
  }

  public ngOnInit(): void {
    this.ngControl.control!.addValidators(this.isDateWithinBounds());
    this.ngControl.control!.addValidators(this.isDepartureBeforeArrival());
    if (this.ngControl.value) {
      this.onDateChange(new Date(this.ngControl.value));
      this.cdRef.detectChanges();
    }
    this.subscription.add(
      this.ngControl.control!.valueChanges.subscribe(date => {
        if (date) {
          this.dateForm.setValue(
            {
              day: this.cleanDay(date.getDate() + ''),
              month: this.cleanMonth(date.getMonth() + 1 + ''),
              year: date.getFullYear() + '',
              hour: this.cleanHours(date.getHours() + ''),
              minutes: this.cleanMinutes(date.getMinutes() + ''),
            },
            { emitEvent: false }
          );
        }
        this.innerControl.setValue(this.formatDate());
        if (this.departureOrArrival.value === ERelationType.arrival) {
          this.arrivalControl()?.setValue(this.ngControl.value);
        }
        if (this.departureOrArrival.value === ERelationType.departure) {
          this.departureControl()?.setValue(this.ngControl.value);
        }
        this.cdRef.detectChanges();
      })
    );
    this.subscription.add(
      this.dateForm.controls.day.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 31) {
          cleanedValue = '31';
        }
        this.dateForm.controls.day.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.month.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 12) {
          cleanedValue = '12';
        }
        this.dateForm.controls.month.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.year.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 4) {
          // if more than this is needed we have different problems
          cleanedValue = value[0] + value[1] + value[2] + value[3];
        }
        this.dateForm.controls.year.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.hour.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 23) {
          cleanedValue = '23';
        }
        this.dateForm.controls.hour.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.minutes.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 59) {
          cleanedValue = '59';
        }
        this.dateForm.controls.minutes.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.valueChanges.subscribe(dateForm => {
        this.setDate(
          dateForm.day,
          dateForm.month,
          dateForm.year,
          dateForm.hour,
          dateForm.minutes
        );
      })
    );
    this.subscription.add(
      fromEvent<KeyboardEvent & { target?: { id: string } }>(
        document,
        'keyup'
      ).subscribe(event => {
        const isCorrectTarget = event.target?.id?.includes(this.id());
        if (isCorrectTarget) {
          event.preventDefault();
          if (event.key === ' ') {
            this.showCalendar.set(true);
          }
        } else if (!isCorrectTarget && this.showCalendar()) {
          event.preventDefault();
          this.showCalendar.set(false);
          this.resetDate();
        }
        if (event.key === 'Escape' && this.showCalendar()) {
          this.showCalendar.set(false);
          this.resetDate();
        }
      })
    );
    this.subscription.add(
      fromEvent<MouseEvent & { target?: { id: string } }>(
        document,
        'mouseup'
      ).subscribe(event => {
        const isCorrectTarget = event?.target?.id?.includes(this.id());
        if (!isCorrectTarget && this.showCalendar()) {
          this.showCalendar.set(false);
          this.resetDate();
        }
      })
    );
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.ngControl.control!.removeValidators(this.isDateWithinBounds());
  }

  public formatDate(): string {
    const date = this.dateForm.value;
    if (!date.year || !date.month || !date.day) {
      return '';
    }
    const dateString = `${date.year}-${date.month}-${date.day}`;
    return this.showTimePicker()
      ? dateString + ` ${date.hour}:${date.minutes}`
      : dateString;
  }

  protected setDate(
    day?: string,
    month?: string,
    year?: string,
    hour: string = '00',
    minutes: string = '00'
  ): void {
    this.innerControl.setValue(this.formatDate());
    if (day && month && year) {
      this.ngControl.control!.setValue(
        new Date(`${month}.${day}.${year} ${hour}:${minutes}`)
      );
      this.ngControl.control!.updateValueAndValidity();
    }
  }

  protected onDateChange(date: Date): void {
    this.ngControl.control!.setValue(date);
    this.dateForm.setValue({
      day: this.cleanDay(date.getDate() + ''),
      month: this.cleanMonth(date.getMonth() + 1 + ''),
      year: date.getFullYear() + '',
      hour: this.cleanHours(date.getHours() + ''),
      minutes: this.cleanMinutes(date.getMinutes() + ''),
    });
    this.ngControl.control!.markAsTouched();
    this.innerControl.setValue(this.formatDate());
  }

  protected onDateConfirm(date: Date): void {
    this.onDateChange(date);
    this.showCalendar.set(false);
  }

  protected cleanNumber(value: string): string {
    const isNumber = new RegExp('^[0-9]*$');
    return value
      .split('')
      .filter(character => isNumber.test(character))
      .join('');
  }

  protected cleanDay(value: string | undefined): string {
    if (!value) {
      return '01';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 31) {
      cleanedValue = '01';
    }
    if (+cleanedValue < 10 && +cleanedValue > 0 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '01';
    }
    return cleanedValue;
  }

  protected cleanMonth(value: string | undefined): string {
    if (!value) {
      return '01';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 12) {
      cleanedValue = '01';
    }
    if (+cleanedValue < 10 && +cleanedValue > 0 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '01';
    }
    return cleanedValue;
  }

  protected cleanHours(value: string | undefined): string {
    if (!value) {
      return '00';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 23) {
      cleanedValue = '00';
    }
    if (+cleanedValue < 10 && +cleanedValue > 0 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '00';
    }
    return cleanedValue;
  }

  protected cleanMinutes(value: string | undefined): string {
    if (!value) {
      return '00';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 59) {
      cleanedValue = '00';
    }
    if (+cleanedValue < 10 && +cleanedValue >= 1 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '00';
    }
    return cleanedValue;
  }

  protected closeCalendar(): void {
    this.showCalendar.set(false);
    this.resetDate();
  }

  protected resetDate(): void {
    this.arrivalControl()?.setValue(this.originalArrival);
    this.departureControl()?.setValue(this.originalDeparture);
  }

  protected clear(): void {
    this.originalArrival = null;
    this.originalDeparture = null;
    this.arrivalControl()?.reset();
    this.departureControl()?.reset();
    this.ngControl.reset();
  }

  private isDateWithinBounds(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value === ''
      ) {
        return null;
      }

      const today: Date = control.value;
      const minDate: Date | undefined = this.min();
      const maxDate: Date | undefined = this.max();

      if (minDate) {
        return today < minDate ? { dateMin: true } : null;
      }
      if (maxDate) {
        return today > maxDate ? { dateMax: true } : null;
      }
      return null;
    };
  }

  private isDepartureBeforeArrival(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const arrival: Date | undefined = this.arrivalControl()
        ? new Date(this.arrivalControl()!.value!)
        : undefined;
      const departure: Date | undefined = this.departureControl()
        ? new Date(this.departureControl()!.value!)
        : undefined;
      if (
        !arrival ||
        !departure ||
        control.value === undefined ||
        control.value === null ||
        control.value === ''
      ) {
        return null;
      }

      return arrival.getTime() <= departure.getTime()
        ? { arrivalBeforeDeparture: true }
        : null;
    };
  }

  public editArrivalDate(): void {
    this.departureOrArrival.setValue(ERelationType.arrival);
    this.ngControl.control!.setValue(
      this.arrivalControl()?.value ?? new Date()
    );
  }

  public editDepartureDate(): void {
    this.departureOrArrival.setValue(ERelationType.departure);
    this.ngControl.control!.setValue(
      this.departureControl()?.value ?? new Date()
    );
  }

  public pickNow(): void {
    this.ngControl.control!.setValue(new Date());
  }

  public pickIn15Minutes(): void {
    const today = new Date();
    this.ngControl.control!.setValue(
      new Date(today.getTime() + ONE_MINUTE * 15)
    );
  }

  public pickIn60Minutes(): void {
    const today = new Date();
    this.ngControl.control!.setValue(new Date(today.getTime() + ONE_HOUR));
  }
}
